import { useId, useState, type InputHTMLAttributes } from 'react';

/**
 * TTTextField: uppercase label, filled surface-alt input with no visible border,
 * 12px radius, 2px accent ring on focus. The password variant gets a reveal toggle
 * with a real accessible name — icon-only controls without one are a DoD failure
 * in this design system (docs/22 §7.2).
 */
interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string | undefined;
}

export function TextField({ label, error, type = 'text', ...rest }: Props) {
  const id = useId();
  const errorId = `${id}-error`;
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  const effectiveType = isPassword && revealed ? 'text' : type;

  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="text-[12px] font-bold uppercase tracking-[0.05em] text-ink-soft"
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={effectiveType}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={
            'w-full rounded-sm bg-surface-alt px-4 py-[15px] text-[16px] text-ink ' +
            'outline-none placeholder:text-ink-faint ' +
            'focus:ring-2 focus:ring-accent ' +
            (isPassword ? 'pr-14 ' : '')
          }
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? 'Скрыть пароль' : 'Показать пароль'}
            // Drawn 34px like every other round control, but the hit area is pushed out to 44
            // with an invisible pseudo-element rather than by growing the circle — the same
            // trick `IconButton` uses, and the same guideline (docs/22 §7.2).
            className="absolute right-2 top-1/2 grid h-[34px] w-[34px] -translate-y-1/2 place-items-center rounded-full text-ink-soft before:absolute before:-inset-[5px] before:content-['']"
          >
            <EyeIcon crossed={revealed} />
          </button>
        )}
      </div>
      {error && (
        <p id={errorId} className="text-[12px] text-accent-soft-ink">
          {error}
        </p>
      )}
    </div>
  );
}

function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z" />
      <circle cx="12" cy="12" r="2.75" />
      {crossed && <path d="M4 20 20 4" />}
    </svg>
  );
}
